import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CategoryStoreSelectors, DevicesRouterStoreSelectors } from '@era/stores';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { DeviceAssignmentHistoryItemDto } from '@rocksoft/settings-sdk';
import { combineLatest, map, Observable } from 'rxjs';
import { DeviceAssignmentHistoryStoreSelectors } from '../device-assignment-history-store';
import { isChildCategoryType } from '../device-assignment-history-store/device-assignment-history.state';

interface AssignmentHistoryRow extends DeviceAssignmentHistoryItemDto {
  categoryDisplayText: string;
}

@Component({
  selector: 'era-assignment-history-modal',
  templateUrl: './assignment-history-modal.container.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AsyncPipe, DatePipe],
})
export class AssignmentHistoryModalComponent {
  public readonly activeModal = inject(NgbActiveModal);
  private readonly store = inject(Store);

  public readonly assignments$: Observable<AssignmentHistoryRow[]>;
  public readonly deviceId$: Observable<string | null>;

  constructor() {
    this.assignments$ = combineLatest([
      this.store.select(DeviceAssignmentHistoryStoreSelectors.selectDeviceAssignmentHistoryForCurrentUdid),
      this.store.select(CategoryStoreSelectors.selectAllCategoriesByType('Location')),
    ]).pipe(
      map(([history, locations]) => {
        const locationNames = new Map(
          locations.map((location) => [location.categoryId, location.categoryDisplayName] as const)
        );

        return history.map((assignment) => ({
          ...assignment,
          categoryDisplayText: getCategoryDisplayText(assignment, locationNames),
        }));
      })
    );
    this.deviceId$ = this.store.select(DevicesRouterStoreSelectors.selectUdid);
  }
}

function getCategoryDisplayText(
  assignment: DeviceAssignmentHistoryItemDto,
  locationNames: ReadonlyMap<string, string>
): string {
  if (assignment.categoryProblem?.title === 'CategoryDeleted') {
    return 'Deleted category';
  }

  if (assignment.categoryProblem?.title === 'CategoryNotFound') {
    return 'Category unavailable';
  }

  if (!assignment.category || assignment.category.id === null) {
    return 'Unassigned';
  }

  const category = assignment.category;
  const categoryName = category.displayName?.trim() || category.categoryType;

  if (category.categoryType === 'Location') {
    const locationName = locationNames.get(category.id) ?? category.displayName?.trim();
    return locationName ? `${locationName} | Spare` : 'Spare';
  }

  if (!isChildCategoryType(category.categoryType)) {
    return categoryName;
  }

  const locationName = category.parentCategoryId ? locationNames.get(category.parentCategoryId) : undefined;
  return locationName ? `${locationName} | ${categoryName}` : categoryName;
}
